import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLogin from './components/AdminLogin';
import AdminSidebar from './components/AdminSidebar';
import MessagesTab from './components/MessagesTab';
import AppointmentsTab from './components/AppointmentsTab';
import StatsTab from './components/StatsTab';
import VehiculosTab from './components/VehiculosTab';
import ConfigTab from './components/ConfigTab';
import SocialTab from './components/SocialTab';
import AlquilerRequestsTab from './components/AlquilerRequestsTab';
import EquipoAdminTab from './components/EquipoAdminTab';
import ServiciosPreciosTab from './components/ServiciosPreciosTab';
import AnuncioAdminTab from './components/AnuncioAdminTab';
import FacturasTab from './components/FacturasTab';

type Tab =
  | 'messages'
  | 'appointments'
  | 'alquiler-requests'
  | 'stats'
  | 'vehiculos'
  | 'equipo'
  | 'servicios-precios'
  | 'anuncio'
  | 'social'
  | 'facturas'
  | 'config';

const TAB_TITLES: Record<Tab, { label: string; icon: string }> = {
  messages:              { label: 'Mensajes',             icon: 'ri-mail-line' },
  appointments:          { label: 'Citas',                icon: 'ri-calendar-line' },
  'alquiler-requests':   { label: 'Solicitudes Alquiler', icon: 'ri-steering-2-line' },
  stats:                 { label: 'Estadísticas',          icon: 'ri-bar-chart-box-line' },
  vehiculos:             { label: 'Vehículos',             icon: 'ri-car-line' },
  equipo:                { label: 'Equipo',                icon: 'ri-team-line' },
  'servicios-precios':   { label: 'Servicios y Precios',   icon: 'ri-price-tag-3-line' },
  anuncio:               { label: 'Anuncio',               icon: 'ri-megaphone-line' },
  social:                { label: 'Social',                icon: 'ri-video-line' },
  facturas:              { label: 'Facturas',              icon: 'ri-file-text-line' },
  config:                { label: 'Configuración',         icon: 'ri-settings-3-line' },
};

const MOBILE_NAV_TABS: Tab[] = ['messages', 'appointments', 'alquiler-requests', 'vehiculos', 'config'];

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => sessionStorage.getItem('bali_admin') === '1');
  const [activeTab, setActiveTab] = useState<Tab>('messages');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingApts, setPendingApts] = useState(0);
  const [alert, setAlert] = useState<{ type: 'message' | 'appointment'; text: string } | null>(null);

  const showAlert = useCallback((type: 'message' | 'appointment', text: string) => {
    setAlert({ type, text });
    setTimeout(() => setAlert(null), 5000);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    const channel = supabase
      .channel('admin-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'TALLERMECANICOKEVIN_contact_messages' },
        (payload) => {
          setUnreadMessages((p) => p + 1);
          const nombre = (payload.new as { nombre?: string }).nombre ?? 'Alguien';
          showAlert('message', `Nuevo mensaje de ${nombre}`);
        }
      )
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'TALLERMECANICOKEVIN_appointments' },
        (payload) => {
          setPendingApts((p) => p + 1);
          const nombre = (payload.new as { nombre?: string }).nombre ?? 'Alguien';
          showAlert('appointment', `Nueva cita de ${nombre}`);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [isLoggedIn, showAlert]);

  const handleLogin = () => { sessionStorage.setItem('bali_admin', '1'); setIsLoggedIn(true); };
  const handleLogout = () => { sessionStorage.removeItem('bali_admin'); setIsLoggedIn(false); };

  if (!isLoggedIn) return <AdminLogin onLogin={handleLogin} />;

  const current = TAB_TITLES[activeTab];

  const handleTabChange = (t: string) => {
    setActiveTab(t as Tab);
    setSidebarOpen(false);
  };

  return (
    <div className="flex h-screen bg-zinc-950 overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">
        <AdminSidebar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          unreadMessages={unreadMessages}
          pendingAppointments={pendingApts}
          onLogout={handleLogout}
          mobile={false}
        />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="relative z-10 w-72 h-full">
            <AdminSidebar
              activeTab={activeTab}
              onTabChange={handleTabChange}
              unreadMessages={unreadMessages}
              pendingAppointments={pendingApts}
              onLogout={handleLogout}
              mobile
              onClose={() => setSidebarOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top header */}
        <header className="flex-shrink-0 bg-zinc-900 border-b border-zinc-800 px-4 md:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-white cursor-pointer"
              onClick={() => setSidebarOpen(true)}
            >
              <i className="ri-menu-3-line text-xl" />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className={`${current.icon} text-yellow-400 text-base`} />
              </div>
              <div>
                <h1 className="text-white font-bold text-sm tracking-wide">{current.label}</h1>
                <p className="text-zinc-600 text-[10px] hidden sm:block">Taller Kevin · Admin</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {alert && (
              <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 border text-xs font-bold animate-pulse rounded ${
                alert.type === 'message'
                  ? 'bg-yellow-400/10 border-yellow-400/30 text-yellow-400'
                  : 'bg-green-500/10 border-green-500/30 text-green-400'
              }`}>
                <i className={`${alert.type === 'message' ? 'ri-mail-line' : 'ri-calendar-line'} text-sm`} />
                <span className="whitespace-nowrap">{alert.text}</span>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="w-9 h-9 flex items-center justify-center border border-zinc-800 text-zinc-500 hover:text-red-400 hover:border-red-500/30 transition-all cursor-pointer"
              title="Cerrar sesión"
            >
              <i className="ri-logout-box-line text-base" />
            </button>
          </div>
        </header>

        {/* Scrollable content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-20 md:pb-6">
          {activeTab === 'messages'          && <MessagesTab onBadgeUpdate={setUnreadMessages} />}
          {activeTab === 'appointments'      && <AppointmentsTab onBadgeUpdate={setPendingApts} />}
          {activeTab === 'alquiler-requests' && <AlquilerRequestsTab />}
          {activeTab === 'stats'             && <StatsTab />}
          {activeTab === 'vehiculos'         && <VehiculosTab />}
          {activeTab === 'equipo'            && <EquipoAdminTab />}
          {activeTab === 'servicios-precios' && <ServiciosPreciosTab />}
          {activeTab === 'anuncio'            && <AnuncioAdminTab />}
          {activeTab === 'social'            && <SocialTab />}
          {activeTab === 'facturas'          && <FacturasTab />}
          {activeTab === 'config'            && <ConfigTab />}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-zinc-900 border-t border-zinc-800 flex overflow-x-auto">
        {MOBILE_NAV_TABS.map((id) => {
          const { label, icon } = TAB_TITLES[id];
          const isActive = activeTab === id;
          const badge = id === 'messages' ? unreadMessages : id === 'appointments' ? pendingApts : 0;
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-shrink-0 flex-1 min-w-[60px] flex flex-col items-center justify-center py-2 gap-0.5 cursor-pointer relative transition-colors ${
                isActive ? 'text-yellow-400' : 'text-zinc-500'
              }`}
            >
              <div className="relative w-5 h-5 flex items-center justify-center">
                <i className={`${icon} text-lg`} />
                {badge > 0 && (
                  <span className="absolute -top-1 -right-1.5 min-w-4 h-4 flex items-center justify-center bg-yellow-400 text-black text-[9px] font-black rounded-full px-0.5">
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </div>
              <span className="text-[9px] font-bold uppercase tracking-wide whitespace-nowrap">{label.split(' ')[0]}</span>
              {isActive && <div className="absolute top-0 left-0 right-0 h-0.5 bg-yellow-400" />}
            </button>
          );
        })}
        {/* More button opens sidebar */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="flex-shrink-0 flex-1 min-w-[60px] flex flex-col items-center justify-center py-2 gap-0.5 cursor-pointer text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-more-2-fill text-lg" />
          </div>
          <span className="text-[9px] font-bold uppercase tracking-wide whitespace-nowrap">Más</span>
        </button>
      </nav>
    </div>
  );
};

export default AdminPage;
